import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import mermaid from 'mermaid';

interface ModalData {
  title: string;
  desc: string;
  flow: string;
  links?: Array<{
    label: string;
    href: string;
  }>;
}

interface AxisModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ModalData | null;
}

const AxisModal: React.FC<AxisModalProps> = ({ isOpen, onClose, data }) => {
  const mermaidRef = useRef<HTMLDivElement>(null);

  // Initialize Mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'base',
      themeVariables: {
        primaryColor: '#3b82f6',
        primaryTextColor: '#1e3a8a',
        primaryBorderColor: '#60a5fa',
        lineColor: '#93c5fd',
        secondaryColor: '#dbeafe',
        tertiaryColor: '#eff6ff',
        fontSize: '14px',
      },
    });
  }, []);

  // Render Mermaid diagram when data changes
  useEffect(() => {
    if (isOpen && data?.flow && mermaidRef.current) {
      const renderDiagram = async () => {
        try {
          mermaidRef.current!.innerHTML = '';
          const { svg } = await mermaid.render(
            `mermaid-${Date.now()}`,
            data.flow
          );
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = svg;
          }
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = '<p class="text-red-500 text-sm">Erreur de rendu du diagramme</p>';
          }
        }
      };
      renderDiagram();
    }
  }, [isOpen, data?.flow]);

  // Close on Escape key and prevent background scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Block scroll on both html and body
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen, onClose]);

  if (!data) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - fond reste net sans blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40"
            style={{ zIndex: 99998 }}
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 99999 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide"
              style={{
                /* Liquid Glass Effect comme l'image de référence */
                background: 'rgba(255, 255, 255, 0.65)',
                backdropFilter: 'blur(25px) saturate(200%)',
                WebkitBackdropFilter: 'blur(25px) saturate(200%)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 px-6 py-4 rounded-t-2xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">
                      {data.title}
                    </h2>
                    {/* Links in header */}
                    {data.links && data.links.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {data.links.map((link, i) => (
                          <a
                            key={i}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          >
                            {link.label}
                            <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-full ml-4"
                    style={{
                      background: 'rgba(0, 0, 0, 0.05)',
                    }}
                    aria-label="Fermer la modal"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Description avec support HTML riche */}
                <div
                  className="prose prose-lg max-w-none"
                  style={{
                    lineHeight: '1.8',
                  }}
                >
                  <style>{`
                    .prose h3 {
                      font-size: 1.25rem;
                      font-weight: 700;
                      color: #1e3a8a;
                      margin-top: 2rem;
                      margin-bottom: 1rem;
                      padding-left: 1rem;
                      border-left: 4px solid #3b82f6;
                      background: linear-gradient(to right, rgba(59, 130, 246, 0.08), transparent);
                      padding-top: 0.5rem;
                      padding-bottom: 0.5rem;
                      border-radius: 0 0.25rem 0.25rem 0;
                    }
                    .prose p {
                      color: #374151;
                      margin-bottom: 1rem;
                    }
                    .prose ul {
                      list-style: none;
                      padding-left: 0;
                      margin: 1rem 0;
                      background: rgba(249, 250, 251, 0.5);
                      padding: 1rem;
                      border-radius: 0.5rem;
                    }
                    .prose li {
                      color: #4b5563;
                      padding-left: 1.5rem;
                      margin-bottom: 0.75rem;
                      position: relative;
                    }
                    .prose li:before {
                      content: "•";
                      position: absolute;
                      left: 0.5rem;
                      color: #3b82f6;
                      font-weight: bold;
                      font-size: 1.2em;
                    }
                    .prose hr {
                      border: none;
                      height: 2px;
                      background: linear-gradient(to right, transparent, #3b82f6, transparent);
                      margin: 2rem 0;
                      opacity: 0.3;
                    }
                    .prose strong {
                      color: #1e3a8a;
                      font-weight: 600;
                      background: linear-gradient(to right, rgba(59, 130, 246, 0.1), transparent);
                      padding: 0.1rem 0.3rem;
                      border-radius: 0.2rem;
                    }
                    .prose em {
                      color: #4b5563;
                      font-style: italic;
                      background: rgba(251, 191, 36, 0.1);
                      padding: 0.1rem 0.3rem;
                      border-radius: 0.2rem;
                    }
                    .prose .section-divider {
                      text-align: center;
                      color: #d1d5db;
                      font-weight: 300;
                      margin: 2rem 0;
                      letter-spacing: 0.5em;
                    }
                    .prose p:first-child {
                      font-size: 1.05rem;
                      color: #1f2937;
                    }
                  `}</style>
                  <div dangerouslySetInnerHTML={{ __html: data.desc }} />
                </div>

                {/* Flow (Mermaid diagram) */}
                {data.flow && (
                  <div className="rounded-lg p-6"
                    style={{
                      background: 'rgba(249, 250, 251, 0.5)',
                      border: '1px solid rgba(209, 213, 219, 0.5)',
                    }}
                  >
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Architecture Flow
                    </h3>
                    <div
                      ref={mermaidRef}
                      className="mermaid-container overflow-x-auto"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                      }}
                    />
                  </div>
                )}

                {/* Close button */}
                <div className="flex justify-end pt-4"
                  style={{
                    borderTop: '1px solid rgba(209, 213, 219, 0.3)',
                  }}
                >
                  <button
                    onClick={onClose}
                    className="px-6 py-3 text-white rounded-lg hover:shadow-lg transition-all duration-300 font-medium"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AxisModal;
